import { ErrandActionResponse } from '@backend/schemas/src/errand-actions'
import { ErrandResponse } from '@backend/schemas/src/errands'

type ErrandStatus = ErrandResponse['status']
type ErrandDocument = ErrandResponse['documents'][number]
type ErrandNote = ErrandResponse['notes'][number]

/**
 * The part of a thread message the timeline needs. Automated messages are the assistant's
 * proactive reactions; `sourceDocuments` records which documents each was written about, which is
 * also what tells us which documents arrived in the same upload. `sourceNotes` is the same record
 * for notes, and is what lets a note row link into the chat message that reacted to it.
 */
export type ReactionMessage = {
  automated?: boolean
  automatedAt?: string
  id: string
  sourceDocuments?: { id: string; name: string }[]
  sourceNotes?: { id: string; title: string }[]
}

export type ErrandTimelineEvent =
  | { id: string; at: string; kind: 'created' }
  | {
      id: string
      at: string
      from: ErrandStatus
      kind: 'status-change'
      messageId?: string
      to: ErrandStatus
    }
  | {
      id: string
      at: string
      documents: ErrandDocument[]
      kind: 'upload'
      messageId?: string
      /** No reaction covers these yet — the upload is still moving through the pipeline. */
      pending: boolean
    }
  | { id: string; action: ErrandActionResponse; at: string; kind: 'draft' }
  | { id: string; at: string; kind: 'note'; messageId?: string; note: ErrandNote }

const reactions = (messages: ReactionMessage[]) =>
  messages
    .filter(message => message.automated && message.automatedAt)
    .sort((a, b) => a.automatedAt!.localeCompare(b.automatedAt!))

/**
 * One row per status TRANSITION.
 *
 * The first history entry is the status the errand was created with, which the "created" row
 * already says — rendering it too gives a new errand two rows with the same timestamp. It stays
 * in the history because it is what lets the next entry show a real "from -> to" instead of a
 * bare "status set to X", so it is skipped here rather than never written.
 */
const statusChangeEvents = (
  errand: ErrandResponse,
  messages: ReactionMessage[],
): ErrandTimelineEvent[] => {
  const history = errand.statusHistory ?? []
  const sorted = [...history].sort((a, b) => a.changedAt.localeCompare(b.changedAt))
  const automated = reactions(messages)

  return sorted.slice(1).map((entry, i) => ({
    at: entry.changedAt,
    // `i` indexes the sliced array, so `sorted[i]` is this entry's predecessor.
    from: sorted[i]!.status,
    id: `status-${entry.changedAt}-${i}`,
    kind: 'status-change',
    // The first reaction written at or after the change is the one that reacted to it. One
    // reaction can cover a status change and an upload at once, in which case both rows point at
    // the same message — correct, since it is one message about both.
    messageId: automated.find(message => message.automatedAt! >= entry.changedAt)?.id,
    to: entry.status,
  }))
}

/**
 * One row per UPLOAD rather than per document, so uploading four files reads as one thing that
 * happened instead of four.
 *
 * The grouping comes from the reactions themselves: each automated message records the documents
 * it was written about, and those are exactly the ones that arrived together — the backend only
 * reacts once an upload's whole batch has settled. Documents no reaction covers yet are the
 * upload still in progress, and collapse into a single pending row.
 */
const uploadEvents = (
  errand: ErrandResponse,
  messages: ReactionMessage[],
): ErrandTimelineEvent[] => {
  const byId = new Map(errand.documents.map(document => [document.id, document]))
  const grouped: ErrandTimelineEvent[] = []
  const claimed = new Set<string>()

  for (const message of reactions(messages)) {
    const documents = (message.sourceDocuments ?? [])
      .map(source => byId.get(source.id))
      .filter((document): document is ErrandDocument => Boolean(document))

    for (const document of documents) claimed.add(document.id)
    if (documents.length === 0) continue

    grouped.push({
      // Dated by when the documents were created, not when the assistant got round to reacting —
      // otherwise the upload would sort after status changes that actually happened later.
      at: [...documents].map(document => document.createdAt).sort()[0]!,
      documents,
      id: `upload-${message.id}`,
      kind: 'upload',
      messageId: message.id,
      pending: false,
    })
  }

  const unclaimed = errand.documents.filter(document => !claimed.has(document.id))
  if (unclaimed.length === 0) return grouped

  return [
    ...grouped,
    {
      at: [...unclaimed].map(document => document.createdAt).sort()[0]!,
      documents: unclaimed,
      id: 'upload-pending',
      kind: 'upload',
      pending: true,
    },
  ]
}

const draftEvents = (actions: ErrandActionResponse[]): ErrandTimelineEvent[] =>
  actions.map(action => ({ action, at: action.createdAt, id: `draft-${action.id}`, kind: 'draft' }))

/**
 * One row per note attached to the errand (via the "Lägg till anteckning" button), anchored to the
 * chat message that reacted to it once its background analysis has finished — the same way an
 * upload row anchors to the reaction about the documents.
 *
 * Rows for notes still being analysed simply have no `messageId` yet: unlike an upload, that isn't
 * worth its own "still working" state, because the note's text is already fully visible to the
 * user on the note itself while the AI is only deriving a title and a timeline from it.
 */
const noteEvents = (
  errand: ErrandResponse,
  messages: ReactionMessage[],
): ErrandTimelineEvent[] => {
  const reactedBy = new Map<string, string>()

  for (const message of reactions(messages)) {
    for (const source of message.sourceNotes ?? []) reactedBy.set(source.id, message.id)
  }

  // `?? []`: falls back cleanly while the backend that adds `notes` to the response hasn't been
  // deployed yet — an older API response simply omits the field rather than sending `[]`.
  return (errand.notes ?? []).map(note => ({
    at: note.createdAt,
    id: `note-${note.id}`,
    kind: 'note',
    messageId: reactedBy.get(note.id),
    note,
  }))
}

/**
 * Merges a synthetic "errand created" event with status-change, upload, note, and draft events into one
 * chronological (newest-first) list — the "Händelser" timeline on the errand page. Pure
 * client-side merge: every source is already fetched by the page, so there's no dedicated backend
 * endpoint for this. The created event is always present (it's just `errand.createdAt`, not
 * fabricated), so the list is never empty and always ends on how the errand began.
 */
export const buildErrandTimeline = (
  errand: ErrandResponse,
  actions: ErrandActionResponse[],
  messages: ReactionMessage[] = [],
): ErrandTimelineEvent[] =>
  [
    { at: errand.createdAt, id: 'created', kind: 'created' } as const,
    ...statusChangeEvents(errand, messages),
    ...uploadEvents(errand, messages),
    ...draftEvents(actions),
    ...noteEvents(errand, messages),
  ].sort((a, b) => b.at.localeCompare(a.at))
